import type {
  AggregateRecovery,
  LatestPlanSnapshot,
  MealLog,
  PlanDay,
  RecoveryMetricsMap,
  ReminderSettings,
  WearableConnection,
  WorkoutSession,
} from "./types";

export function uuid(): string {
  return globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function localDateKey(value: Date | string = new Date()): string {
  const date = value instanceof Date ? value : new Date(value);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

export function formatDate(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" },
): string {
  if (!value) return "—";
  const date = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat(undefined, options).format(date);
}

export function formatDuration(seconds: number | null | undefined): string {
  const safe = Math.max(0, Math.floor(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  return [hours, minutes, secs].map((part) => String(part).padStart(2, "0")).join(":");
}

export function planDays(snapshot: LatestPlanSnapshot | null): PlanDay[] {
  return snapshot?.plan.weekly_schedule?.flatMap((week) => week.days || []) || [];
}

export function todayPlanDay(snapshot: LatestPlanSnapshot | null, now = new Date()): PlanDay | null {
  const days = planDays(snapshot);
  const today = localDateKey(now);
  return days.find((day) => day.date === today)
    || days.find((day) => day.type !== "Rest" && new Date(`${day.date}T23:59:59`) >= now)
    || days.find((day) => day.type !== "Rest")
    || null;
}

export function completedPlanDates(sessions: WorkoutSession[]): Set<string> {
  return new Set(
    sessions
      .filter((session) => session.status === "completed" && session.plan_date)
      .map((session) => session.plan_date),
  );
}

export function todayMeals(meals: MealLog[], now = new Date()): MealLog[] {
  const today = localDateKey(now);
  return meals.filter((meal) => localDateKey(meal.logged_at) === today);
}

// Recovery API keys are snake_case while AggregateRecovery uses UI-friendly names.
export function aggregateRecoveryMetrics(
  wearables: WearableConnection[],
  metrics: RecoveryMetricsMap,
): AggregateRecovery {
  const values = Object.values(metrics).filter((item) => !item.error);
  const average = (key: "sleep_hours" | "hrv" | "resting_hr" | "readiness_score" | "weekly_distance_km") => {
    const available = values
      .map((item) => item[key])
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    return available.length
      ? available.reduce((sum, value) => sum + value, 0) / available.length
      : null;
  };
  const readiness = average("readiness_score");
  return {
    avgSleepHours: average("sleep_hours"),
    avgHRV: average("hrv"),
    restingHR: average("resting_hr"),
    readinessScore: readiness == null ? null : Math.round(readiness),
    recentWeeklyDistanceKm: average("weekly_distance_km"),
    connectedSources: wearables.filter((item) => item.connected).map((item) => item.provider),
  };
}

export function exportPlanCalendar(snapshot: LatestPlanSnapshot | null): boolean {
  const days = planDays(snapshot).filter((day) => day.type !== "Rest");
  if (!days.length) return false;

  const escapeIcs = (value: unknown) => String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/([,;])/g, "\\$1")
    .replace(/\n/g, "\\n");

  const events = days.map((day) => {
    const date = day.date.replaceAll("-", "");
    return [
      "BEGIN:VEVENT",
      `UID:${uuid()}@fittracker`,
      `DTSTART;VALUE=DATE:${date}`,
      `DTEND;VALUE=DATE:${date}`,
      `SUMMARY:${escapeIcs(day.title || day.type)}`,
      `DESCRIPTION:${escapeIcs(day.workout || day.notes)}`,
      "END:VEVENT",
    ].join("\r\n");
  }).join("\r\n");

  const blob = new Blob([
    `BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//FitTracker//Training Plan//EN\r\n${events}\r\nEND:VCALENDAR\r\n`,
  ], { type: "text/calendar" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "fittracker-plan.ics";
  link.click();
  URL.revokeObjectURL(link.href);
  return true;
}

const REMINDER_KEY = "fittracker.reminder";

export function readReminderSettings(): ReminderSettings {
  const fallback: ReminderSettings = { enabled: false, time: "07:00", lastDate: "" };
  try {
    const parsed = JSON.parse(localStorage.getItem(REMINDER_KEY) || "null") as Partial<ReminderSettings> | null;
    return {
      enabled: Boolean(parsed?.enabled),
      time: typeof parsed?.time === "string" ? parsed.time : fallback.time,
      lastDate: typeof parsed?.lastDate === "string" ? parsed.lastDate : "",
    };
  } catch {
    localStorage.removeItem(REMINDER_KEY);
    return fallback;
  }
}

export function writeReminderSettings(settings: ReminderSettings): void {
  localStorage.setItem(REMINDER_KEY, JSON.stringify(settings));
}

export function providerLabel(provider: string): string {
  return ({ strava: "Strava", oura: "Oura", ultrahuman: "Ultrahuman" } as Record<string, string>)[provider]
    || provider;
}
